import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import PageLayout from '../../components/PageLayout';
import { useAuth } from '../../context/AuthContext';
import { db } from '../../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  serverTimestamp
} from 'firebase/firestore';
import { checkEntityReferences } from '../../utils/deletionSafeguard';
import { invalidateMaster, COLL } from '../../utils/masterDataStore';
import { colors, spacing, borderRadius, shadows, typography } from '../../theme/colors';
import {
  FiPlus, FiEdit2, FiTrash2, FiBriefcase, FiSearch, FiX,
  FiCalendar, FiDollarSign, FiUser, FiCheckCircle, FiClock, FiXCircle
} from 'react-icons/fi';
import useDocumentShortcuts from '../../hooks/useDocumentShortcuts';
import usePagePermissions from '../../hooks/usePagePermissions';
import useCurrency from '../../hooks/useCurrency';
import useEscapeClose from '../../hooks/useEscapeClose';

const Jobs = () => {
  const { activeCompany, userProfile } = useAuth();
  const { canEdit, canDelete, canChange } = usePagePermissions('masterData', 'jobs');
  const { formatCurrency } = useCurrency();
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Modal states
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingJob, setEditingJob] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    jobCode: '',
    jobName: '',
    customerId: '',
    description: '',
    startDate: '',
    endDate: '',
    budgetAmount: 0,
    status: 'Active',
    isActive: true
  });

  useEffect(() => {
    if (activeCompany?.id) {
      fetchAllData();
    }
  }, [activeCompany]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchJobs(), fetchCustomers()]);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
    setLoading(false);
  };

  const fetchJobs = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/jobs`),
      orderBy('jobCode')
    );
    const snapshot = await getDocs(q);
    setJobs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const fetchCustomers = async () => {
    const q = query(
      collection(db, `companies/${activeCompany.id}/customers`),
      orderBy('companyName')
    );
    const snapshot = await getDocs(q);
    setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
  };

  const generateJobCode = () => {
    const maxCode = jobs.reduce((max, job) => {
      const num = parseInt(job.jobCode?.replace('JOB-', '')) || 0;
      return num > max ? num : max;
    }, 0);
    return `JOB-${String(maxCode + 1).padStart(4, '0')}`;
  };

  const handleAddJob = () => {
    setFormData({
      jobCode: generateJobCode(),
      jobName: '',
      customerId: '',
      description: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      budgetAmount: 0,
      status: 'Active',
      isActive: true
    });
    setEditingJob(null);
    setShowModal(true);
  };

  const handleEditJob = (job) => {
    if (!canChange) { alert('You can add new records but cannot change saved ones.'); return; } // Add-only role (2026-11-04)
    setFormData({
      jobCode: job.jobCode,
      jobName: job.jobName || '',
      customerId: job.customerId || '',
      description: job.description || '',
      startDate: job.startDate || '',
      endDate: job.endDate || '',
      budgetAmount: job.budgetAmount || 0,
      status: job.status || 'Active',
      isActive: job.isActive !== false
    });
    setEditingJob(job);
    setShowModal(true);
  };

  const handleSaveJob = async () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!formData.jobName.trim()) {
      alert('Job name is required');
      return;
    }

    try {
      const jobData = {
        ...formData,
        budgetAmount: parseFloat(formData.budgetAmount) || 0,
        updatedAt: serverTimestamp()
      };

      if (editingJob) {
        await updateDoc(doc(db, `companies/${activeCompany.id}/jobs`, editingJob.id), jobData);
      } else {
        jobData.createdAt = serverTimestamp();
        jobData.createdBy = userProfile?.uid;
        jobData.actualCost = 0;
        jobData.actualRevenue = 0;
        await addDoc(collection(db, `companies/${activeCompany.id}/jobs`), jobData);
      }
      setShowModal(false);
      // Jobs are cached for the transaction forms — push the change out now.
      invalidateMaster(activeCompany.id, COLL.jobs);
      fetchJobs();
    } catch (error) {
      console.error('Error saving job:', error);
      alert('Failed to save job');
    }
  };

  // Keyboard shortcuts: Ctrl+N to open add modal, Ctrl+S to save
  useDocumentShortcuts({
    onNew: handleAddJob,
    onSave: handleSaveJob,
    viewMode: showModal ? 'form' : 'list',
    saving: false
  });

  const handleDeleteJob = async (job) => {
    if (!canDelete) { alert('You do not have permission to delete.'); return; }
    if (window.confirm(`Are you sure you want to delete job "${job.jobName}"?`)) {
      try {
        const { hasReferences, message } = await checkEntityReferences(activeCompany?.id, 'job', job.id);
        if (hasReferences) { alert(message); return; }
        await deleteDoc(doc(db, `companies/${activeCompany.id}/jobs`, job.id));
        invalidateMaster(activeCompany.id, COLL.jobs);
        fetchJobs();
      } catch (error) {
        console.error('Error deleting job:', error);
        alert('Failed to delete job');
      }
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.companyName || '-';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return colors.success.main;
      case 'Completed': return colors.info.main;
      case 'Cancelled': return colors.error.main;
      case 'On Hold': return colors.warning.main;
      default: return colors.text.secondary;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Active': return <FiClock />;
      case 'Completed': return <FiCheckCircle />;
      case 'Cancelled': return <FiXCircle />;
      default: return <FiClock />;
    }
  };

  // Filter jobs
  const filteredJobs = jobs.filter(job => {
    const matchesSearch =
      job.jobCode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.jobName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCustomerName(job.customerId).toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || job.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Stats
  const activeJobs = jobs.filter(j => j.status === 'Active').length;
  const completedJobs = jobs.filter(j => j.status === 'Completed').length;
  const totalBudget = jobs.reduce((sum, j) => sum + (j.budgetAmount || 0), 0);

  return (
    <PageLayout pageTitle="Jobs / Projects" breadcrumb="Master Data / Jobs">
      <Container>
        <HeaderSection>
          <SearchBox>
            <FiSearch />
            <input
              type="text"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm('')}>
                <FiX />
              </ClearButton>
            )}
          </SearchBox>
          <FilterGroup>
            <FilterSelect
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="Active">Active</option>
              <option value="Completed">Completed</option>
              <option value="On Hold">On Hold</option>
              <option value="Cancelled">Cancelled</option>
            </FilterSelect>
          </FilterGroup>
          {canEdit && (
            <AddButton onClick={handleAddJob}>
              <FiPlus /> Add Job
            </AddButton>
          )}
        </HeaderSection>

        <StatsRow>
          <StatCard>
            <StatIcon $color={colors.primary.main}><FiBriefcase /></StatIcon>
            <StatInfo>
              <StatValue>{jobs.length}</StatValue>
              <StatLabel>Total Jobs</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.success.main}><FiClock /></StatIcon>
            <StatInfo>
              <StatValue>{activeJobs}</StatValue>
              <StatLabel>Active</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.info.main}><FiCheckCircle /></StatIcon>
            <StatInfo>
              <StatValue>{completedJobs}</StatValue>
              <StatLabel>Completed</StatLabel>
            </StatInfo>
          </StatCard>
          <StatCard>
            <StatIcon $color={colors.warning.main}><FiDollarSign /></StatIcon>
            <StatInfo>
              <StatValue>{formatCurrency(totalBudget)}</StatValue>
              <StatLabel>Total Budget</StatLabel>
            </StatInfo>
          </StatCard>
        </StatsRow>

        {loading ? (
          <LoadingMessage>Loading jobs...</LoadingMessage>
        ) : (
          <TableContainer>
            {filteredJobs.length === 0 ? (
              <EmptyMessage>
                {searchTerm || statusFilter !== 'all'
                  ? 'No jobs found matching your criteria.'
                  : 'No jobs found. Click "Add Job" to create one.'}
              </EmptyMessage>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Job Code</Th>
                    <Th>Job Name</Th>
                    <Th>Customer</Th>
                    <Th>Duration</Th>
                    <Th>Budget</Th>
                    <Th>Status</Th>
                    <Th>Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredJobs.map(job => (
                    <Tr key={job.id}>
                      <Td>
                        <JobCode>{job.jobCode}</JobCode>
                      </Td>
                      <Td>
                        <JobName>{job.jobName}</JobName>
                        {job.description && (
                          <JobDescription>{job.description}</JobDescription>
                        )}
                      </Td>
                      <Td>
                        {job.customerId ? (
                          <CustomerBadge>
                            <FiUser size={12} />
                            {getCustomerName(job.customerId)}
                          </CustomerBadge>
                        ) : (
                          <span style={{ color: colors.text.secondary }}>-</span>
                        )}
                      </Td>
                      <Td>
                        <DurationInfo>
                          <FiCalendar size={12} />
                          {formatDate(job.startDate)}
                          {job.endDate && ` - ${formatDate(job.endDate)}`}
                        </DurationInfo>
                      </Td>
                      <Td>
                        <BudgetAmount>{formatCurrency(job.budgetAmount)}</BudgetAmount>
                      </Td>
                      <Td>
                        <StatusBadge $color={getStatusColor(job.status)}>
                          {getStatusIcon(job.status)}
                          {job.status}
                        </StatusBadge>
                      </Td>
                      <Td>
                        <ActionButtons>
                          {canChange && (
                            <ActionButton onClick={() => handleEditJob(job)} title="Edit">
                              <FiEdit2 />
                            </ActionButton>
                          )}
                          {canDelete && (
                            <ActionButton $danger onClick={() => handleDeleteJob(job)} title="Delete">
                              <FiTrash2 />
                            </ActionButton>
                          )}
                        </ActionButtons>
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}
          </TableContainer>
        )}
      </Container>

      {/* Modal */}
      {showModal && (
        <ModalOverlay>
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <h3>{editingJob ? 'Edit Job' : 'Add New Job'}</h3>
              <CloseButton onClick={() => setShowModal(false)}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            <ModalBody>
              <FormRow>
                <FormGroup>
                  <label>Job Code</label>
                  <input
                    type="text"
                    value={formData.jobCode}
                    disabled
                  />
                </FormGroup>
                <FormGroup>
                  <label>Job Name *</label>
                  <input
                    type="text"
                    value={formData.jobName}
                    onChange={(e) => setFormData({ ...formData, jobName: e.target.value })}
                    placeholder="Enter job/project name"
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label>Customer</label>
                  <select
                    value={formData.customerId}
                    onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                  >
                    <option value="">Select Customer (Optional)</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.companyName}
                      </option>
                    ))}
                  </select>
                </FormGroup>
                <FormGroup>
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    <option value="Active">Active</option>
                    <option value="On Hold">On Hold</option>
                    <option value="Completed">Completed</option>
                    <option value="Cancelled">Cancelled</option>
                  </select>
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  />
                </FormGroup>
                <FormGroup>
                  <label>End Date</label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  />
                </FormGroup>
              </FormRow>

              <FormRow>
                <FormGroup>
                  <label>Budget Amount ({activeCompany?.currencyCode || 'PKR'})</label>
                  <input
                    type="number"
                    value={formData.budgetAmount}
                    onChange={(e) => setFormData({ ...formData, budgetAmount: e.target.value })}
                    placeholder="0"
                  />
                </FormGroup>
                <FormGroup>
                  <label>Active</label>
                  <select
                    value={formData.isActive ? 'yes' : 'no'}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.value === 'yes' })}
                  >
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </FormGroup>
              </FormRow>

              <FormGroup>
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter job description"
                  rows={3}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <CancelButton onClick={() => setShowModal(false)}>Cancel</CancelButton>
              {canEdit && (
                <SaveButton onClick={handleSaveJob}>
                  {editingJob ? 'Update' : 'Save'}
                </SaveButton>
              )}
            </ModalFooter>
          </Modal>
        </ModalOverlay>
      )}
    </PageLayout>
  );
};

// Styled Components
const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${spacing.md};
  flex-wrap: wrap;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.sm};
  background: ${colors.background.default};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  padding: ${spacing.sm} ${spacing.md};
  flex: 1;
  max-width: 300px;

  svg {
    color: ${colors.text.secondary};
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: ${typography.fontSize.sm};
    background: transparent;
  }
`;

const ClearButton = styled.button`
  background: transparent;
  color: ${colors.text.secondary};
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.sm};

  &:hover {
    background: ${colors.background.hover};
  }
`;

const FilterGroup = styled.div`
  display: flex;
  gap: ${spacing.sm};
`;

const FilterSelect = styled.select`
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  background: ${colors.background.default};
  color: ${colors.text.primary};
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
  }
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${spacing.xs};
  padding: ${spacing.sm} ${spacing.md};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing.md};
`;

const StatCard = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md};
  background: ${colors.background.default};
  padding: ${spacing.md};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${borderRadius.md};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const StatInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const StatValue = styled.span`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
`;

const StatLabel = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
`;

const TableContainer = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
  overflow: hidden;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
  background: ${colors.background.default};
  border-radius: ${borderRadius.md};
  border: 1px solid ${colors.border.light};
`;

const EmptyMessage = styled.div`
  text-align: center;
  padding: ${spacing.xl};
  color: ${colors.text.secondary};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: ${spacing.md};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  background: ${colors.background.paper};
  border-bottom: 1px solid ${colors.border.light};
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.border.light};
  transition: background 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: ${spacing.md};
  vertical-align: middle;
`;

const JobCode = styled.span`
  font-family: monospace;
  font-size: ${typography.fontSize.sm};
  color: ${colors.primary.main};
  background: ${colors.primary.main}10;
  padding: 4px 8px;
  border-radius: ${borderRadius.sm};
  font-weight: ${typography.fontWeight.medium};
`;

const JobName = styled.div`
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
`;

const JobDescription = styled.div`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  margin-top: 2px;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CustomerBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
`;

const DurationInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.secondary};

  svg {
    color: ${colors.text.secondary};
  }
`;

const BudgetAmount = styled.span`
  font-weight: ${typography.fontWeight.medium};
  color: ${colors.text.primary};
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: ${borderRadius.sm};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  background: ${props => props.$color}15;
  color: ${props => props.$color};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${spacing.xs};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '15' : colors.background.hover};
    color: ${props => props.$danger ? colors.error.main : colors.primary.main};
  }
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing.md};
`;

const Modal = styled.div`
  background: ${colors.background.default};
  border-radius: ${borderRadius.lg};
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${spacing.md} ${spacing.lg};
  border-bottom: 1px solid ${colors.border.light};

  h3 {
    font-size: ${typography.fontSize.lg};
    font-weight: ${typography.fontWeight.semibold};
    color: ${colors.text.primary};
    margin: 0;
  }
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  color: ${colors.text.secondary};
  border-radius: ${borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${spacing.md};

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};

  label {
    font-size: ${typography.fontSize.sm};
    font-weight: ${typography.fontWeight.medium};
    color: ${colors.text.secondary};
  }

  input, select, textarea {
    padding: ${spacing.sm} ${spacing.md};
    border: 1px solid ${colors.border.main};
    border-radius: ${borderRadius.md};
    font-size: ${typography.fontSize.sm};
    color: ${colors.text.primary};
    background: ${colors.background.default};
    transition: all 0.2s ease;

    &:focus {
      outline: none;
      border-color: ${colors.primary.main};
      box-shadow: 0 0 0 3px ${colors.primary.main}20;
    }

    &:disabled {
      background: ${colors.background.paper};
      cursor: not-allowed;
    }
  }

  textarea {
    resize: vertical;
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing.sm};
  padding: ${spacing.md} ${spacing.lg};
  border-top: 1px solid ${colors.border.light};
`;

const CancelButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: transparent;
  color: ${colors.text.secondary};
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.background.hover};
    border-color: ${colors.text.secondary};
  }
`;

const SaveButton = styled.button`
  padding: ${spacing.sm} ${spacing.lg};
  background: ${colors.primary.main};
  color: ${colors.text.white};
  border-radius: ${borderRadius.md};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.medium};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.primary.dark};
  }
`;

export default Jobs;
